import utils from '@eitje/utils'
import _ from 'lodash'
import {useEffect, useState, useMemo} from 'react'
import {onSortingChange, onGroupingChange, onUnpinnedColumnOrderChange, finishResetTable} from '../../actions'
import {useCache} from '../use_cache'
import {useReactTable} from '@tanstack/react-table'
import {useCachedUserPreferences, columnVisibilityObject, getRowId, SETTINGS, usePersistPinnedOrder} from './helpers'
import {getDefaultSorting, getStoredSorting} from '../../helpers'

export const useAdvancedTable = instanceTaxonomy => {
	// Destructure taxonomy of the AdvancedTable instance.
	const {TABLE_INFO} = instanceTaxonomy
	const {tableName, fields: allTableColumns, initialPinning, defaultSortColumn, defaultSortDesc} = TABLE_INFO
	const {visibleColumns, pinnedColumns, unpinnedColumns, groupedColumn, cachedSorting} = useCachedUserPreferences(TABLE_INFO)

	const defaultSorting = useMemo(
		() => getDefaultSorting({allTableColumns, groupedColumn, defaultSortColumn, defaultSortDesc}),
		[allTableColumns, groupedColumn, defaultSortColumn, defaultSortDesc],
	)
	const initialSorting = useMemo(() => getStoredSorting({cachedSorting, defaultSorting}), [cachedSorting, defaultSorting])

	// Define states. The states themselves may be shared through the context but often not reused in other places in the
	// application, their main purpose besides sharing information is to trigger renders on changes.
	const [columnVisibility, setColumnVisibility] = useState({})
	const [sorting, setSort] = useState(initialSorting)
	const [grouping, setGrouping] = useState([groupedColumn])
	const [columnOrder, setColumnOrder] = useState(unpinnedColumns)
	const [expanded, setExpanded] = useState({})
	const _setExpanded = fn => _.isFunction(fn) && setExpanded(fn)
	const savedOrTaxonomyPinned = utils.exists(pinnedColumns) ? pinnedColumns : initialPinning
	const [columnPinning, setColumnPinning] = useState({left: savedOrTaxonomyPinned})

	// Create a context with the instance's current state to reuse through all other AdvancedTable files and components.
	const state = useMemo(() => {
		return {sorting, columnVisibility, grouping, expanded, columnPinning, columnOrder, setColumnPinning}
	}, [expanded, grouping, columnVisibility, sorting, columnPinning, columnOrder])

	// Initialize the table
	const advancedTableInstance = useReactTable({
		...SETTINGS,
		...instanceTaxonomy,
		meta: TABLE_INFO,
		state,
		getRowId,
		columnIds: allTableColumns,
		onColumnVisibilityChange: setColumnVisibility,
		onSortingChange: sort => onSortingChange({sort, setSort, instanceTaxonomy}),
		getSubRows: row => row.subRows,
		onExpandedChange: _setExpanded,
		onColumnPinningChange: setColumnPinning,
		onColumnOrderChange: order => onUnpinnedColumnOrderChange({order, setState: setColumnOrder, tableName}), // only triggered when UNPINNED columns ordered!
	})

	// A 'resetTable' flag is used to prevent infinity renders. Resetting both means editing the tanStack cache (below) and our own Redux cache.
	const {resetTable} = useCache({tableName})

	useEffect(() => {
		if (resetTable) {
			advancedTableInstance.resetColumnOrder()
			advancedTableInstance.resetColumnPinning()
			setColumnPinning({left: initialPinning})
			setSort(defaultSorting)
			finishResetTable({tableName})
		}
	}, [resetTable, advancedTableInstance, initialPinning, tableName, defaultSorting])

	// See fn definition for a description of the complicated logic for ordering pinned columns.
	usePersistPinnedOrder({pinnedColumns, columnPinning, tableName})

	useEffect(() => {
		// Use effect to hide columns based on the user's cached preferences.
		advancedTableInstance.setColumnVisibility(() => columnVisibilityObject({allTableColumns, visibleColumns}))
	}, [visibleColumns])

	useEffect(
		// Use effect to group the table based on the user's cached preferences.
		() =>
			onGroupingChange({
				groupedColumn,
				setGrouping,
				onSortingChange,
				setSort,
				instanceTaxonomy,
				advancedTableInstance,
				setColumnPinning,
			}),
		[groupedColumn],
	)

	return advancedTableInstance
}
